import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Form } from "@gigpro/ui/Form";
import { MaskedTextfield, wageMaskProps } from "@gigpro/ui/Form/MaskedTextfield";
import { Select, SelectItem } from "@gigpro/ui/Form/Select";
import { Switch } from "@gigpro/ui/Form/Switch";
import { Textarea } from "@gigpro/ui/Form/Textarea";
import { Textfield } from "@gigpro/ui/Form/Textfield";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { Input } from "@gigpro/ui/Input";
import { Label } from "@gigpro/ui/Label";
import { Stepper, StepperContent, StepperIndicator, StepperItem, StepperNav, StepperPanel } from "@gigpro/ui/Stepper";
import { toast } from "@gigpro/ui/Toast";
import { useIntervalEffect } from "@react-hookz/web";
import { ApiError } from "@sidegigllc/api";
import {
  hireJobsWorkingInterviewSeriesCreate,
  useHireJobsScrapeByUrlCreate,
  type JobPostingResponse,
  type PaginatedJobPostingResponseListStatusCounts as JobStatusCounts,
  useHireJobsExperienceLevelsList,
  useProContractorTypeList,
  useSkillsList,
} from "@sidegigllc/api/client";
import {
  useActiveLocationManagers,
  useJobInterviewAvailability,
  usePostJob,
  useSaveJobInterviewAvailability,
  useUpdateJob,
} from "api/queries/hire";
import { type JobPost, jobPostSchema } from "api/resources/hire";
import { jobAvailabilitySchema } from "domains/hire/availabilitySchema";
import type { ShiftConversion } from "domains/hire/components/ConvertibleShiftList/seats";
import { JobInterviewAvailabilityField } from "domains/hire/components/JobInterviewAvailabilityField";
import type { WorkingInterviewChoice } from "domains/hire/components/WorkingInterviewStep";
// oxlint-disable-next-line import/no-cycle -- invite, new-job, live and send-offer dialogs chain into each other by design
import { JobLiveStepContainer } from "domains/hire/containers/JobLiveStep";
import { JobsTrialStepContainer } from "domains/hire/containers/JobsTrialStep";
import { WorkingInterviewStepContainer } from "domains/hire/containers/WorkingInterviewStep";
import { useCanPostWorkingInterviews } from "domains/hire/hooks/useCanPostWorkingInterviews";
import { convertShifts } from "domains/hire/hooks/useConvertShiftsToWorkingInterviews";
import { useInterviewAvailabilityEnabled } from "domains/hire/hooks/useInterviewAvailabilityEnabled";
import { useJobsGateState } from "domains/hire/hooks/useJobsGateState";
import { useScreeningQuestionnaireToggleEnabled } from "domains/hire/hooks/useScreeningQuestionnaireToggleEnabled";
import { isJobsLocked } from "domains/hire/jobsPlan";
import { availabilityWriteFor, postedJobCount, sameWindows } from "domains/hire/utils";
import { useFeatureFlag } from "providers/LaunchDarkly";
import * as React from "react";
import { FormProvider, type SubmitHandler, useFormState, useWatch } from "react-hook-form";
import type { ModalBaseProps } from "types/utils";
import { captureException } from "utils/errorReporting";
import { mapScrapeResponseToJobFields } from "utils/mapScrapeResponse";
import { jobSavedMessage, withMergedNote } from "./availabilityWrite";

const POPULATE_PHRASES = ["Reading", "Thinking", "Noodling", "Extracting"] as const;
const POPULATE_PHRASE_INTERVAL_MS = 1200;

// Strips the read-only nested object; a null hiring_manager_id is omitted because the API rejects it.
const toJobPayload = (data: JobPost): JobPost => {
  const { hiring_manager, hiring_manager_id, ...rest } = data;
  return { ...rest, ...(hiring_manager_id != null && { hiring_manager_id }) };
};

// The job is saved and can't roll back, so a shift that didn't convert is a message, not a thrown error.
const persistConversions = async (jobId: string, conversions: Array<ShiftConversion>) => {
  const { converted, errors } = await convertShifts(jobId, conversions);
  if (errors.length === 0) return null;
  // A refusal (the shift changed since the list loaded) isn't a bug; a 5xx or network failure is.
  for (const error of errors) if (ApiError.fromThrown(error).isUnknownError()) captureException(error);
  return converted === 0
    ? `Job posted. The working interviews weren't created: ${ApiError.fromThrown(errors[0]).getMainErrorMessage()}`
    : `Job posted, but only ${converted} of ${conversions.length} shifts became working interviews. Check the job page for the rest.`;
};

interface NewJobDialogProps extends ModalBaseProps {
  locationId: number;
  job?: JobPost;
  onSelect?: (job: JobPostingResponse) => void;
  statusCounts?: JobStatusCounts;
  /** A posted shift to turn into this job's working interview: its role fills the form and the shift starts picked. */
  initialShift?: { skillId: number; gigId: number };
}

type DialogBodyProps = Omit<NewJobDialogProps, "open"> & {
  /** Called once a job is saved; the wrapper decides between closing and the "job is live" step. */
  onSaved: (job: JobPostingResponse) => void;
};

const DialogBody = ({ job, locationId, onSelect, onClose, onSaved, statusCounts, initialShift }: DialogBodyProps) => {
  const screeningQuestionnaireEnabled = useScreeningQuestionnaireToggleEnabled();
  const interviewAvailabilityEnabled = useInterviewAvailabilityEnabled();
  const workingInterviewsEnabled = useCanPostWorkingInterviews(locationId);
  // Its own form because availability is not a `job_postings` field: it persists to
  // `/hire/manager-availability/jobs/{id}/week/`, a separate resource keyed by the posting, and it
  // validates on its own rules (overlap, end-after-start).
  const { data: jobAvailability } = useJobInterviewAvailability(job?.id, interviewAvailabilityEnabled);
  const { save: saveJobAvailability, clearOverride } = useSaveJobInterviewAvailability();
  // New posting has no override: editor starts empty, inherits until a window's added.
  const availabilityForm = useZodForm({
    schema: jobAvailabilitySchema,
    defaultValues: { windows: [] },
  });
  // Read during render, not inside a handler: RHF only tracks `isDirty` for subscribers.
  const { isDirty: isAvailabilityDirty } = useFormState({ control: availabilityForm.control });
  const [step, setStep] = React.useState(1);
  // Posting only (edits use the Manage menu); working interviews replace it, so the job inherits the default.
  const showAvailabilityStep = interviewAvailabilityEnabled && !workingInterviewsEnabled && !job?.id;
  // Only when posting: an open job gets the same option from its own page (decision 5).
  const showWorkingInterviewStep = workingInterviewsEnabled && !job?.id;
  // Numbered in order, so with both flags off the steps are exactly details then review.
  const stepOrder = [
    "details",
    ...(showAvailabilityStep ? ["availability" as const] : []),
    ...(showWorkingInterviewStep ? ["workingInterviews" as const] : []),
    "review",
  ] as const;
  const stepNumber = (key: (typeof stepOrder)[number]) => {
    const index = stepOrder.indexOf(key);
    return index === -1 ? undefined : index + 1;
  };
  const availabilityStep = stepNumber("availability");
  const workingInterviewStep = stepNumber("workingInterviews");
  const reviewStep = stepOrder.length;
  const stepAfter = (current: number) => Math.min(current + 1, reviewStep);
  const stepBefore = (current: number) => Math.max(current - 1, 1);
  const [populateLink, setPopulateLink] = React.useState("");
  const [isPopulating, setIsPopulating] = React.useState(false);
  const [populateWordIndex, setPopulateWordIndex] = React.useState(0);
  useIntervalEffect(
    () => setPopulateWordIndex((i) => (i + 1) % POPULATE_PHRASES.length),
    isPopulating ? POPULATE_PHRASE_INTERVAL_MS : undefined,
  );
  // Holds the position id whose default-description fill should be skipped once,
  // so populating from a scraped URL doesn't clobber the scraped description on
  // the next render. Keyed by id (not a bool) so a no-op or job-matching scrape
  // can't leave the guard stuck and swallow a later manual position change.
  const skipDefaultDescriptionForPositionRef = React.useRef<number | null>(null);

  // Disable refetchOnWindowFocus since these are used for form values - refetching could overwrite user input
  const { data: experienceLevels } = useHireJobsExperienceLevelsList({ query: { refetchOnWindowFocus: false } });
  const { data: positions } = useSkillsList(
    { license: "hire", include_defaults: true, query: "ordered_by_popularity" },
    { query: { refetchOnWindowFocus: false } },
  );
  const { data: contractTypes } = useProContractorTypeList(
    { license: "hire" },
    { query: { refetchOnWindowFocus: false, select: (data) => data.results } },
  );
  const scrapeByUrl = useHireJobsScrapeByUrlCreate();
  const { data: hiringManagers } = useActiveLocationManagers(locationId);

  // Compute default values reactively - updates automatically when async data loads
  const defaultJobTypeId = job?.job_type_id || contractTypes?.find((c) => c.name.includes("Full-time"))?.id;
  const defaultLevelId = job?.experience_level_id || experienceLevels?.find((c) => c.name.includes("Mid-level"))?.id;

  const form = useZodForm({
    schema: jobPostSchema,
    values: {
      status: "open",
      location: locationId,
      ...(!job && initialShift && { position: initialShift.skillId }),
      ...job,
      // Always set these to ensure defaults override null/0 values from job
      job_type_id: defaultJobTypeId,
      experience_level_id: defaultLevelId,
      // Prefill the manager select from the nested read object (hiring_manager_id isn't returned).
      hiring_manager_id: job?.hiring_manager?.id,
      // New postings default ON only once the toggle is live; edit reflects the saved value.
      requires_screening_questionnaire: job?.requires_screening_questionnaire ?? screeningQuestionnaireEnabled,
    } as JobPost,
  });

  // Keyed by the posting, so it can only be written once the job exists.
  const persistAvailability = async (jobId: string, { isCreate }: { isCreate: boolean }) => {
    const { windows } = availabilityForm.getValues();
    const write = availabilityWriteFor({
      isDirty: isAvailabilityDirty,
      // Override = having windows; empty inherits the default again.
      isOverride: windows.length > 0,
      storedIsOverride: jobAvailability?.isOverride ?? false,
    });
    if (!showAvailabilityStep || write === "none") return { mergedWindows: false };
    try {
      if (write === "clear") await clearOverride(jobId);
      else {
        const persisted = await saveJobAvailability(jobId, windows);
        // The service merges touching windows, so it can keep fewer than were sent.
        return { mergedWindows: !sameWindows(persisted, windows) };
      }
    } catch {
      // The job is already saved, so this cannot roll back — say what did not stick.
      const preamble = isCreate ? "Job posted" : "Job updated";
      toast.error(
        write === "clear"
          ? `${preamble}, but its interview availability still uses this job's own windows. You can reset it on the job.`
          : `${preamble}, but its interview availability didn't save. You can set it on the job.`,
      );
    }
    return { mergedWindows: false };
  };

  // Also keyed by the posting, so it runs once the job exists. The job is saved either way.
  // Returns the one message to show instead of the success toast, or null when nothing failed.
  const persistWorkingInterviews = async (jobId: string): Promise<string | null> => {
    // Without full access the picks are not stored: posting from the choice card leaves the job with its regular shifts.
    if (!showWorkingInterviewStep || !workingInterviewChoice || !hasFullAccessRef.current) return null;
    if (workingInterviewChoice.kind === "convert") return persistConversions(jobId, workingInterviewChoice.conversions);
    try {
      await hireJobsWorkingInterviewSeriesCreate(jobId, workingInterviewChoice.request);
      return null;
    } catch (error) {
      // The job is saved and can't roll back, so name what didn't stick and why (often no manager phone).
      captureException(error);
      return `Job posted. The working interviews weren't created: ${ApiError.fromThrown(error).getMainErrorMessage()}`;
    }
  };

  const handleOnSuccess = async (newJob: JobPostingResponse) => {
    const totalJobs = postedJobCount(statusCounts);
    const isFirstJob = totalJobs === 0;
    const isCreatingNewJob = !job?.id;

    // Before the confirmation, which reports whether the server merged anything.
    const { mergedWindows } = await persistAvailability(newJob.id, { isCreate: isCreatingNewJob });
    const workingInterviewFailure = await persistWorkingInterviews(newJob.id);
    // One honest message: a success toast on top of the failure made it look like everything worked.
    if (workingInterviewFailure) toast.warning(withMergedNote(workingInterviewFailure, mergedWindows));
    else toast.success(jobSavedMessage({ isCreate: isCreatingNewJob, isFirstJob, mergedWindows }));

    onSelect?.(newJob);
    // No manual reset: Radix unmounts this body on close, and resetting here flashed step 1
    // during the exit animation.
    onSaved(newJob);
  };
  const postJob = usePostJob({ onSuccess: handleOnSuccess });
  const { status: planStatus, gate, isStaff } = useJobsGateState(locationId);
  // A plan neither trial nor resubscribe can fix (Qwick support sets it up) has no gate but is still not paying.
  const hasFullAccess = !!planStatus && !isJobsLocked(planStatus, isStaff);
  // Set when a post starts, since its success callback can outlive the render that read the gate.
  const hasFullAccessRef = React.useRef(false);
  // Set while the trial or Resubscribe step, opened from the step's choice card, is on screen.
  const [startingPlan, setStartingPlan] = React.useState(false);
  const updateJob = useUpdateJob(job?.id ?? "", { onSuccess: handleOnSuccess });

  const { control, setError, setValue } = form;
  const { isSubmitting } = useFormState({ control });

  // Unmount-on-close resets the form; resetting here flashed step 1 mid-animation.
  const handleOnClose = () => onClose();

  const selectedPositionId = useWatch({ control, name: "position" });

  // Null is "skipped": nothing runs after posting.
  const [workingInterviewChoice, setWorkingInterviewChoice] = React.useState<WorkingInterviewChoice | null>(null);
  React.useEffect(() => {
    if (!selectedPositionId || !positions || selectedPositionId === job?.position) return;
    if (skipDefaultDescriptionForPositionRef.current === selectedPositionId) {
      skipDefaultDescriptionForPositionRef.current = null;
      return;
    }

    const position = positions.find((p) => p.id === selectedPositionId);
    if (position?.default_description) {
      setValue("description", position.default_description);
    }
  }, [selectedPositionId, positions, setValue, job?.position]);

  const onSubmit: SubmitHandler<JobPost> = async (data) => {
    // Only Review posts. A step with a lone text input and no submit button (people per shift) would otherwise
    // post on Enter, skipping Review and whatever the step was about to save.
    if (step !== reviewStep) return;
    const payload = toJobPayload(data);
    hasFullAccessRef.current = hasFullAccess;
    try {
      if (job?.id) {
        await updateJob.mutateAsync(payload);
      } else {
        await postJob.mutateAsync(payload);
      }
    } catch (error) {
      const apiError = ApiError.fromThrown(error);
      apiError.setFormErrors(setError);

      // Always go back to step 1 on any error and show toast
      setStep(1);

      if (apiError.error.code === "invalid" || apiError.error.code === "does_not_exist") {
        toast.error("Please check all required fields");
      } else {
        toast.error(job?.id ? "Unable to update job. Please try again." : "Unable to post job. Please try again.");
      }
    }
  };

  const formValues = useWatch({ control });
  const selectedPosition = positions?.find((p) => p.id === formValues.position);
  const selectedJobType = contractTypes?.find((c) => c.id === formValues.job_type_id);
  const selectedExperienceLevel = experienceLevels?.find((l) => l.id === formValues.experience_level_id);

  const handlePopulate = async () => {
    const url = populateLink.trim();
    if (!url) return;
    setPopulateWordIndex(0);
    setIsPopulating(true);
    try {
      const data = await scrapeByUrl.mutateAsync({ data: { url } });
      const fields = mapScrapeResponseToJobFields(data, contractTypes, experienceLevels);
      if (fields.position != null) {
        // Only shield a scraped description from the position-default effect; with
        // no scraped description, let that effect supply the position's default.
        if (fields.description != null) skipDefaultDescriptionForPositionRef.current = fields.position;
        setValue("position", fields.position);
      }
      if (fields.name != null) setValue("name", fields.name);
      if (fields.wage != null) setValue("wage", fields.wage);
      if (fields.description != null) setValue("description", fields.description);
      if (fields.job_type_id != null) setValue("job_type_id", fields.job_type_id);
      if (fields.experience_level_id != null) setValue("experience_level_id", fields.experience_level_id);
      toast.success("Job information populated.");
    } catch (error) {
      const apiError = ApiError.fromThrown(error);
      toast.error(apiError.error.message || "Unable to populate job from link. Please try again.");
    }
    setIsPopulating(false);
  };

  const handleContinue = async () => {
    const isValid = await form.trigger([
      "position",
      "name",
      "wage",
      "description",
      "job_type_id",
      "experience_level_id",
    ]);

    if (isValid) {
      setStep(stepAfter(1));
    }
  };

  // Overlapping or malformed windows have to be resolved here — Review has nothing to show for them and
  // the write happens after the job is already saved.
  const handleAvailabilityContinue = async () => {
    if ((await availabilityForm.trigger()) && availabilityStep) setStep(stepAfter(availabilityStep));
  };

  // The job isn't posted here: once the plan runs, the step shows its builder and Review posts as for any location.
  if (startingPlan) {
    return (
      <JobsTrialStepContainer
        locationId={locationId}
        purpose="workingInterviews"
        submitLabel="Start free trial"
        saveSubmitLabel="Save and start free trial"
        onStarted={() => Promise.resolve(setStartingPlan(false))}
        onBack={() => setStartingPlan(false)}
      />
    );
  }

  return (
    <Form form={form} onSubmit={onSubmit}>
      <Stepper value={step}>
        <StepperNav>
          <StepperItem step={1}>
            <StepperIndicator className="h-1 w-full" />
          </StepperItem>
          {!!availabilityStep && (
            <StepperItem step={availabilityStep}>
              <StepperIndicator className="h-1 w-full" />
            </StepperItem>
          )}
          {!!workingInterviewStep && (
            <StepperItem step={workingInterviewStep}>
              <StepperIndicator className="h-1 w-full" />
            </StepperItem>
          )}
          <StepperItem step={reviewStep}>
            <StepperIndicator className="h-1 w-full" />
          </StepperItem>
        </StepperNav>
        <StepperPanel className="space-y-4 pt-4">
          <StepperContent value={1} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="populate-job-link">Populate Job Information with link</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="populate-job-link"
                  value={populateLink}
                  onChange={(e) => setPopulateLink(e.target.value)}
                  placeholder="https://example.com/job-posting"
                />
                <Button
                  type="button"
                  variant="contained"
                  onClick={handlePopulate}
                  // Gate on the option lists so a scraped job_type / experience_level is
                  // never dropped (and silently replaced by defaults) by mapping before they load.
                  disabled={!populateLink.trim() || isPopulating || !contractTypes || !experienceLevels}
                  className="min-w-32"
                >
                  {isPopulating ? `${POPULATE_PHRASES[populateWordIndex]}…` : "Populate"}
                </Button>
              </div>
            </div>
            <Select
              control={control}
              name="position"
              label="Position type"
              placeholder={positions ? "Line Cook" : "Loading..."}
              disabled={!positions}
              transform={{ input: String, output: Number }}
            >
              {positions?.map((position) => (
                <SelectItem key={position.id} value={String(position.id)}>
                  {position.name}
                </SelectItem>
              ))}
            </Select>
            <Textfield control={control} name="name" label="Job title" placeholder="Line Cook" />
            <MaskedTextfield
              control={control}
              name="wage"
              label="Enter the wage you wish to offer"
              placeholder="$24.00/hr"
              {...wageMaskProps}
            />
            <div>
              <Textarea control={control} name="description" label="Job description" placeholder="Job description" />
            </div>
            <Select
              control={control}
              name="hiring_manager_id"
              label="Hiring manager"
              placeholder={hiringManagers ? "Select a manager" : "Loading..."}
              disabled={!hiringManagers}
              transform={{ input: String, output: Number }}
              optional
            >
              {hiringManagers
                ?.toSorted((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
                .map((manager) => (
                  <SelectItem key={manager.id} value={String(manager.id)}>
                    {manager.name}
                  </SelectItem>
                ))}
            </Select>
            <fieldset className="grid grid-cols-2 gap-4">
              <Select
                control={control}
                name="job_type_id"
                label="Job type"
                transform={{ input: String, output: Number }}
              >
                {contractTypes?.map((contractType) => (
                  <SelectItem key={contractType.id} value={String(contractType.id)}>
                    {contractType.name}
                  </SelectItem>
                ))}
              </Select>
              <Select
                control={control}
                name="experience_level_id"
                label="Experience level"
                transform={{ input: String, output: Number }}
              >
                {experienceLevels?.map((level) => (
                  <SelectItem key={level.id} value={String(level.id)}>
                    {level.name} ({level.description})
                  </SelectItem>
                ))}
              </Select>
            </fieldset>
            <Textfield
              control={control}
              name="external_application_link"
              label="Link to the original job poster"
              placeholder="https://www.indeed.com/job/line-cook-12345"
              optional
            />
            {screeningQuestionnaireEnabled && (
              <Switch
                control={control}
                name="requires_screening_questionnaire"
                label="Require screening questionnaire"
                description={
                  formValues.requires_screening_questionnaire
                    ? "Pros must answer a few questions before applying. The Passed screening tab will show who met the baseline."
                    : "Pros can apply with one tap. No questionnaire is shown, and the Passed screening tab is hidden for this posting."
                }
              />
            )}
            <DialogFooter className="mt-6 grid grid-cols-2 gap-2">
              <Button variant="text" onClick={handleOnClose}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleContinue}>
                Continue
              </Button>
            </DialogFooter>
          </StepperContent>
          {!!availabilityStep && (
            <StepperContent value={availabilityStep} className="space-y-4">
              <p className="text-large font-semibold">Select interview availability for this job</p>
              {/* A nested provider, not a nested `<form>`: this sub-form has its own schema and dirty
                  state, but the job form's submit is what eventually writes it. */}
              <FormProvider {...availabilityForm}>
                <JobInterviewAvailabilityField label={null} hiringManagerUserId={formValues.hiring_manager_id} />
              </FormProvider>
              <DialogFooter className="mt-6 grid grid-cols-2 gap-2">
                <Button variant="text" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button variant="contained" onClick={handleAvailabilityContinue}>
                  Continue
                </Button>
              </DialogFooter>
            </StepperContent>
          )}
          {!!workingInterviewStep && (
            // Kept mounted so going back from Review finds the step as it was left.
            <StepperContent value={workingInterviewStep} forceMount className="space-y-4">
              <WorkingInterviewStepContainer
                locationId={locationId}
                skillId={selectedPositionId}
                preselectedGigId={initialShift?.gigId}
                active={step === workingInterviewStep}
                gate={gate}
                onStartPlan={() => setStartingPlan(true)}
                onBack={() => setStep(stepBefore(workingInterviewStep))}
                onSkip={() => {
                  setWorkingInterviewChoice(null);
                  setStep(reviewStep);
                }}
                onContinue={(choice) => {
                  setWorkingInterviewChoice(choice);
                  setStep(reviewStep);
                }}
              />
            </StepperContent>
          )}
          <StepperContent value={reviewStep} className="space-y-4">
            {/* Review job details */}
            <div className="rounded-sm border border-light-gray bg-white p-5">
              <p className="text-large font-semibold">Review job details</p>

              <div className="mt-2 space-y-0 text-normal">
                <p>Position: {selectedPosition?.name}</p>
                <p>Job title: {formValues.name}</p>
                <p>Type: {selectedJobType?.name}</p>
                <p>
                  Experience: {selectedExperienceLevel?.name} ({selectedExperienceLevel?.description})
                </p>
                <p>Compensation: ${formValues.wage}/hr</p>
              </div>

              {formValues.description && (
                <div className="mt-4 text-normal">
                  <p>Job description:</p>
                  <p className="whitespace-break-spaces">{formValues.description}</p>
                </div>
              )}

              {formValues.external_application_link && (
                <p className="mt-4 text-normal">
                  <span>Link to original job poster: </span>
                  <a
                    href={formValues.external_application_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand underline"
                  >
                    {formValues.external_application_link}
                  </a>
                </p>
              )}
            </div>

            {showWorkingInterviewStep && workingInterviewChoice && hasFullAccess && (
              <div className="rounded-sm border border-light-gray bg-white p-5">
                <p className="text-large font-semibold">Working interviews</p>
                <p className="mt-2 text-normal">{workingInterviewChoice.summary}</p>
              </div>
            )}

            <DialogFooter className="mt-6 grid grid-cols-2 gap-2">
              <Button variant="text" onClick={() => setStep(stepBefore(reviewStep))}>
                Back
              </Button>
              <Button variant="contained" loading={isSubmitting} type="submit">
                {job ? "Update job" : "Post Job"}
              </Button>
            </DialogFooter>
          </StepperContent>
        </StepperPanel>
      </Stepper>
    </Form>
  );
};

const dialogTitle = (postedJob: JobPostingResponse | undefined, job: NewJobDialogProps["job"]) => {
  if (postedJob) return "Job posted";
  return job ? "Edit job" : "Post new job";
};

export const NewJobDialog: React.FC<NewJobDialogProps> = ({ open, ...rest }) => {
  const offerMomentsEnabled = useFeatureFlag("hire-offer-moments");
  // Reset on reopen, not close: resetting on close flashed the form during the exit animation.
  const [postedJob, setPostedJob] = React.useState<JobPostingResponse>();
  const [wasOpen, setWasOpen] = React.useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) setPostedJob(undefined);
  }

  const onSaved = (savedJob: JobPostingResponse) => {
    if (!rest.job?.id && offerMomentsEnabled) setPostedJob(savedJob);
    else rest.onClose();
  };

  return (
    <Dialog open={open} onClose={rest.onClose} className="sm:max-w-2xl">
      <DialogTitle className="pb-0 [&>h2]:text-extraLarge">{dialogTitle(postedJob, rest.job)}</DialogTitle>
      {postedJob ? (
        <JobLiveStepContainer locationId={rest.locationId} job={postedJob} onDone={rest.onClose} />
      ) : (
        <DialogBody {...rest} onSaved={onSaved} />
      )}
    </Dialog>
  );
};
